import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './FarmDiary.css';
import { Link, useSearchParams } from 'react-router-dom';

const API_BASE = '/api';
const statusLabel = {
  PLANNED: '예정',
  IN_PROGRESS: '진행 중',
  COMPLETED: '완료',
  CANCELLED: '취소',
};

const fmt = (v) => (v ? String(v).replace('T', ' ').slice(0, 16) : '-');

const readError = (e, fallback) =>
  e?.response?.data?.message || e?.message || fallback;

const isSmartfarmRequiredError = (e) => {
  const status = e?.response?.status;
  const code = e?.response?.data?.code || e?.response?.data?.error;
  const message = e?.response?.data?.message || '';

  return (
    status === 403 ||
    code === 'SMARTFARM_REQUIRED' ||
    message.includes('스마트팜 등록 후')
  );
};

export default function FarmDiaryDashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [farms, setFarms] = useState([]);
  const farmId = searchParams.get('farm_id') || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessChecking, setAccessChecking] = useState(true);
  const [smartfarmRequired, setSmartfarmRequired] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    setAccessChecking(true);
    setSmartfarmRequired(false);
    setError('');

    axios
      .get(`${API_BASE}/farms`)
      .then((res) => {
        if (cancelled) return;

        const list = res.data?.data || [];
        setFarms(list);

        if (list.length === 0) {
          if (farmId) {
            setSearchParams({}, { replace: true });
          }
          return;
        }

        const selectedExists =
          farmId &&
          list.some((farm) => String(farm.id) === String(farmId));

        if (!selectedExists) {
          setSearchParams(
            { farm_id: String(list[0].id) },
            { replace: true }
          );
        }
      })
      .catch((e) => {
        if (cancelled) return;

        if (isSmartfarmRequiredError(e)) {
          setSmartfarmRequired(true);
          setFarms([]);
          setData(null);

          if (farmId) {
            setSearchParams({}, { replace: true });
          }
          return;
        }

        setError(readError(e, '농장 목록을 불러오지 못했습니다.'));
      })
      .finally(() => {
        if (!cancelled) {
          setAccessChecking(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // 최초 진입 시 농장 목록과 접근 권한을 한 번 확인합니다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (accessChecking || smartfarmRequired) {
      return;
    }

    if (!farmId) {
      setData(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    setLoading(true);
    setError('');

    axios
      .get(`${API_BASE}/dashboard`, {
        params: { farm_id: farmId },
      })
      .then((res) => {
        if (!cancelled) {
          setData(res.data?.data || null);
        }
      })
      .catch((e) => {
        if (cancelled) return;

        if (isSmartfarmRequiredError(e)) {
          setSmartfarmRequired(true);
          setFarms([]);
          setData(null);
          setSearchParams({}, { replace: true });
          return;
        }

        setError(readError(e, '대시보드를 불러오지 못했습니다.'));
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accessChecking, farmId, setSearchParams, smartfarmRequired]);

  const changeFarm = (value) => {
    setData(null);
    setSearchParams(value ? { farm_id: value } : {}, { replace: true });
  };

  const summary = data?.summary || {};
  const env = data?.latest_environment;

  if (accessChecking) {
    return (
      <div
        className="container-fluid py-5 farm-diary-page text-center"
        style={{ maxWidth: 1380 }}
      >
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">확인 중...</span>
        </div>
      </div>
    );
  }

  if (smartfarmRequired) {
    return (
      <div className="container my-2 px-0" style={{ maxWidth: '600px', paddingTop: '20px' }}>
        <h2 className="mb-3 mx-3">영농일지</h2>
        <div className="alert text-secondary mx-2" style={{ background: '#f1f3f5', fontSize: '13px' }}>
          스마트팜 등록 후 영농일지를 이용할 수 있습니다.
        </div>
      </div>
    );
  }

  return (
    <div
      className="container-fluid py-4 farm-diary-page"
      style={{ maxWidth: 1380 }}
    >
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1">영농 대시보드</h2>
          <div className="text-secondary">
            농장 작업, 환경, 장비 동작을 한 화면에서 확인합니다.
          </div>
        </div>

        <div className="d-flex gap-2">
          <Link className="btn btn-outline-secondary" to="/farm-diary/list">
            일지 목록
          </Link>
          <Link
            className="btn btn-success"
            to={`/farm-diary/write${farmId ? `?farm_id=${farmId}` : ''}`}
          >
            + 일지 작성
          </Link>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body d-flex flex-wrap align-items-center gap-3">
          <strong>농장 선택</strong>
          <select
            className="form-select"
            style={{ maxWidth: 360 }}
            value={farmId}
            onChange={(e) => changeFarm(e.target.value)}
          >
            <option value="">농장을 선택하세요</option>
            {farms.map((farm) => (
              <option key={farm.id} value={farm.id}>
                {farm.name}
              </option>
            ))}
          </select>

          {data?.farm?.address && (
            <span className="text-secondary">{data.farm.address}</span>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="py-5 text-center">
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">불러오는 중...</span>
          </div>
        </div>
      ) : !farmId ? (
        <div className="alert alert-info">등록된 농장을 먼저 선택하세요.</div>
      ) : (
        data && (
          <>
            <div className="row g-3 mb-4">
              {[
                ['활성 재배 구역', summary.active_field_count, '개'],
                ['재배 중 작물', summary.active_crop_count, '건'],
                ['오늘 작성 일지', summary.today_diary_count, '건'],
                ['오늘 장비 이벤트', summary.today_device_event_count, '건'],
              ].map(([label, value, unit]) => (
                <div className="col-6 col-xl-3" key={label}>
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body">
                      <div className="text-secondary mb-2">{label}</div>
                      <div className="display-6 fw-bold">
                        {value || 0}
                        <small className="fs-6 ms-1">{unit}</small>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="row g-4">
              <div className="col-12 col-xl-8">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
                    <strong>최근 영농일지</strong>
                    <Link
                      to={`/farm-diary/list?farm_id=${farmId}`}
                      className="btn btn-sm btn-outline-secondary"
                    >
                      전체 보기
                    </Link>
                  </div>

                  <div className="card-body p-0">
                    {(data.recent_diaries || []).length === 0 ? (
                      <div className="p-5 text-center text-secondary">
                        최근 일지가 없습니다.
                      </div>
                    ) : (
                      <div className="list-group list-group-flush">
                        {data.recent_diaries.map((diary) => (
                          <Link
                            key={diary.id}
                            to={`/farm-diary/detail/${diary.id}`}
                            className="list-group-item list-group-item-action py-3"
                          >
                            <div className="d-flex justify-content-between gap-3">
                              <div>
                                <div className="fw-semibold">{diary.title}</div>
                                <small className="text-secondary">
                                  {diary.work_date} ·{' '}
                                  {diary.field_name || '구역 미지정'} ·{' '}
                                  {diary.crop_name || '작물 미지정'}
                                </small>
                              </div>
                              <span className="badge text-bg-light align-self-center">
                                {statusLabel[diary.work_status] ||
                                  diary.work_status}
                              </span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-12 col-xl-4">
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-header bg-white py-3">
                    <strong>최근 환경 스냅샷</strong>
                  </div>

                  <div className="card-body">
                    {!env ? (
                      <div className="text-secondary">
                        저장된 환경정보가 없습니다.
                      </div>
                    ) : (
                      <>
                        <div className="small text-secondary mb-3">
                          {fmt(env.measured_at)} · {env.source}
                        </div>

                        <div className="row g-3 text-center">
                          {[
                            ['온도', env.temperature, '℃'],
                            ['습도', env.humidity, '%'],
                            ['토양수분', env.soil_moisture, '%'],
                            ['조도', env.illuminance, 'lx'],
                          ].map(([label, value, unit]) => (
                            <div className="col-6" key={label}>
                              <div className="bg-light rounded p-3">
                                <div className="small text-secondary">
                                  {label}
                                </div>
                                <div className="fs-4 fw-bold">
                                  {value ?? '-'}
                                  <small className="fs-6">
                                    {value != null ? unit : ''}
                                  </small>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white py-3">
                    <strong>최근 장비 이벤트</strong>
                  </div>

                  <div className="card-body p-0">
                    {(data.recent_device_events || []).length === 0 ? (
                      <div className="p-4 text-secondary">
                        장비 이벤트가 없습니다.
                      </div>
                    ) : (
                      <div className="list-group list-group-flush">
                        {data.recent_device_events.slice(0, 6).map((event) => (
                          <div key={event.id} className="list-group-item">
                            <div className="d-flex justify-content-between">
                              <strong>{event.device_name}</strong>
                              <small>{fmt(event.event_at)}</small>
                            </div>
                            <div className="small text-secondary">
                              {event.event_type} · {event.previous_state || '-'} →{' '}
                              {event.current_state || '-'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}
